import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { ManageAccountContentPage } from '../ManageAccountContentPage/ManageAccountContentPage';
import styles from './Profile.module.scss';
import {
  useGetUserPhoto,
  useLeftPanelActions,
  useSession,
  useUserSettings,
  useUserSettingsUpdate,
} from 'src/hooks';
import { FormInput } from 'src/components/FormInput';
import { FormProvider } from 'src/components/FormProvider';
import { useLazyGetUserByIdQuery } from 'src/store/services';
import { useUserData } from 'src/hooks';
import { UserImage } from 'src/v2/components/UserImage';
import {
  USER_NAME_MAX_LENGTH,
  USER_NAME_MIN_LENGTH,
  USER_NAME_PATTERN_1,
  USER_NAME_PATTERN_2,
} from 'src/constants';
import {
  AppRoutes,
  LeftPanelSections,
  SettingKeys,
  SingleSelectSetting,
} from 'src/types';
import dayjs from 'dayjs';
import { DeleteAccount } from 'src/pages/ManageTasksChatPage/components/AccessAndDataSettings/components/DeleteAccount';
import { SelectSimple } from 'src/components/SelectSimple';
import { SwitchSimple } from 'src/v2/commonComponents/SwitchSimple';
import { ClockCounterClockwise } from '@phosphor-icons/react';
import { Button } from 'src/v2/commonComponents/Button';

type TimeZoneOption = {
  value: string;
  label: string;
  time: string;
};

interface ProfileFormData {
  first_name?: string;
  last_name?: string;
  department_of_team?: string;
  job_title?: string;
}

export const Profile = () => {
  const {
    appUser: { email, user_id, ...restUserInfo },
  } = useSession();
  const { photoUrl } = useGetUserPhoto();
  const { setLeftPanelData } = useLeftPanelActions();

  const [, setImg] = useState('');
  const [errorTooltip, setErrorTooltip] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { updateUserData } = useUserData();

  const { setting: manuallySetting } = useUserSettings<SingleSelectSetting>(
    SettingKeys.MANUALLY_SELECTED_TIMEZONE,
  );

  const { setting: automaticallySetting } =
    useUserSettings<SingleSelectSetting>(
      SettingKeys.IS_TIMEZONE_SET_AUTOMATICALLY,
    );

  const { updateUserSetting } = useUserSettingsUpdate();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore todo remove with "typescript" update - supportedValuesOf supported by TS version >= 5.1
  const listOfTimezones = Intl.supportedValuesOf('timeZone');

  const sortedListOfTimezones = useMemo(() => {
    return listOfTimezones
      .map((item: string) => ({
        label: `${item} ${new Date().toLocaleTimeString('en-US', {
          timeZone: item,
          hour12: true,
          timeStyle: 'short',
        })}`,
        value: item,
        time: new Date().toLocaleString('en-US', { timeZone: item }),
      }))
      .slice()
      .sort((a: TimeZoneOption, b: TimeZoneOption) =>
        dayjs(a.time).isAfter(dayjs(b.time)) ? 1 : -1,
      );
  }, [listOfTimezones]);

  const isAutomatically = !!automaticallySetting?.value;
  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const selectedTimezone = isAutomatically
    ? currentTimezone
    : manuallySetting?.value;

  const handleSwitchChange = async (checked: boolean) => {
    await updateUserSetting({
      settingKey: SettingKeys.IS_TIMEZONE_SET_AUTOMATICALLY,
      value: checked,
    });

    if (checked && manuallySetting?.value !== currentTimezone) {
      await updateUserSetting({
        settingKey: SettingKeys.MANUALLY_SELECTED_TIMEZONE,
        value: currentTimezone,
        hideToast: true,
      });
    }
  };

  const handleSelectChange = async (value: string) => {
    await updateUserSetting({
      settingKey: SettingKeys.MANUALLY_SELECTED_TIMEZONE,
      value,
    });
  };

  useEffect(() => {
    if (photoUrl) {
      setImg(photoUrl);
    }
  }, [photoUrl]);

  const handleChangeUserInfo = async (
    name: 'first_name' | 'last_name',
    value: string,
  ) => {
    const initValue = restUserInfo[name] || '';

    const fieldName = name === 'first_name' ? 'First name' : 'Last name';

    if (value !== '') {
      if (value.length < USER_NAME_MIN_LENGTH) {
        setErrorMessage(`The ${fieldName} is too short`);
        setErrorTooltip(name);
        return;
      }
      if (value.length > USER_NAME_MAX_LENGTH) {
        setErrorMessage(`The ${fieldName} is too long`);
        setErrorTooltip(name);
        return;
      }
      if (!USER_NAME_PATTERN_1.value.test(value)) {
        setErrorMessage(USER_NAME_PATTERN_1.message);
        setErrorTooltip(name);
        return;
      } else if (USER_NAME_PATTERN_2.value.test(value)) {
        setErrorMessage(USER_NAME_PATTERN_2.message);
        setErrorTooltip(name);
        return;
      }
      if (value !== initValue) {
        await updateUserData({ [name]: value });
      }
    } else {
      setErrorMessage(`The ${fieldName} is required`);
      setErrorTooltip(name);
    }
  };

  const [trigger] = useLazyGetUserByIdQuery();

  const methods = useForm<ProfileFormData>({
    defaultValues: async () => {
      const result = await trigger(user_id, true);
      const { data } = result;
      return {
        first_name: data?.first_name || '',
        last_name: data?.last_name || '',
        job_title:
          data?.settings?.profile_setting?.role_information?.job_title || '',
        department_of_team:
          data?.settings?.profile_setting?.role_information
            ?.department_of_team || '',
      };
    },
    mode: 'onBlur',
  });

  const deleteError = () => {
    setErrorTooltip(null);
    setErrorMessage(null);
  };

  const handleOpenHistoryClick = () => {
    setLeftPanelData({
      panelType: LeftPanelSections.CONVERSATION_LIST,
      isExpanded: true,
    });
  };

  return (
    <FormProvider<ProfileFormData> methods={methods}>
      <ManageAccountContentPage
        title="Account"
        subtitle="Manage your Ninja Account"
        isWithBackButton
      >
        <div className={styles.root}>
          <section className={styles.section}>
            <h4 className={styles.title}>Your photo</h4>

            <div className={styles.userAvatarWrapper}>
              <UserImage size={96} />
            </div>

            <hr className={styles.divider} />
          </section>

          <section className={styles.section}>
            <h4 className={styles.title}>Contact information</h4>
            <h5 className={styles.subtitle}>
              Enter your contact information so Ninja can use it when doing
              outreach.
            </h5>

            <div className={styles.group}>
              <div className={styles.field}>
                <label className={styles.label}>
                  First name
                  <span className={styles.rule}>Required</span>
                </label>
                <FormInput
                  name="first_name"
                  type="text"
                  placeholder="First name (e,g. Adam)"
                  onFocus={() => deleteError()}
                  onBlurHandler={(value) =>
                    handleChangeUserInfo('first_name', value)
                  }
                  errorMessage={
                    errorTooltip === 'first_name' && !!errorMessage
                      ? errorMessage
                      : undefined
                  }
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Last name
                  <span className={styles.rule}>Required</span>
                </label>
                <FormInput
                  name="last_name"
                  type="text"
                  placeholder="Last name (e,g. Smith)"
                  onFocus={() => deleteError()}
                  onBlurHandler={(value) =>
                    handleChangeUserInfo('last_name', value)
                  }
                  errorMessage={
                    errorTooltip === 'last_name' && !!errorMessage
                      ? errorMessage
                      : undefined
                  }
                />
              </div>
            </div>

            <div className={styles.group}>
              <div className={`${styles.field} ${styles.full}`}>
                <label className={styles.label}>Email</label>
                <input disabled value={email} />
              </div>
            </div>

            <hr className={styles.divider} />
          </section>

          <div className={styles.root}>
            <h4 className={styles.title}>Timezone</h4>

            <div className={styles.field}>
              <label className={styles.label}>Set timezone automatically</label>
              <SwitchSimple
                isChecked={isAutomatically}
                onChange={handleSwitchChange}
              />
            </div>

            <div className={`${styles.field} ${styles.select}`}>
              <SelectSimple
                aria-label="location-time"
                options={sortedListOfTimezones}
                className={styles.select}
                onChange={handleSelectChange}
                value={selectedTimezone}
                isDisabled={isAutomatically}
              />
            </div>

            <hr className={styles.divider} />

            <h4 className={styles.title}>Legacy Data</h4>
            <div className={styles.legacyData}>
              <div className={styles.legacyDataItem}>
                <ClockCounterClockwise size={20} />
                <p>Access and manage your historical MyNinja data</p>
              </div>

              <Button
                as="link"
                to={AppRoutes.CHAT_HISTORY}
                color="secondary-outline"
                onClick={handleOpenHistoryClick}
              >
                Go to MyNinja History
              </Button>
            </div>

            <hr className={styles.divider} />

            <h4 className={styles.title}>Data control</h4>
            <h5 className={styles.subtitle}>Manage your data preferences.</h5>

            <DeleteAccount />
          </div>
        </div>
      </ManageAccountContentPage>
    </FormProvider>
  );
};
